import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5ockak8q3pa76svmqgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });

        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Form */}
          <div>
            <div className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-semibold mb-4">
              Get In Touch
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Request Your Free Quote
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Fill out the form below and we'll get back to you within 24 hours with a custom quote for your property.
            </p>

            <form id="contact_quote_request" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="(416) 555-1234"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your property and needs
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Property size, specific services needed, any questions..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-red-600 text-white text-lg font-bold rounded-lg hover:bg-red-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Send Quote Request
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                  <p className="text-green-800">
                    Thank you! We'll contact you within 24 hours with your custom quote.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                  <p className="text-red-800">Something went wrong. Please try again or call us directly.</p>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center">
                <i className="ri-lock-line mr-1"></i>
                Your information is secure and will never be shared
              </p>
            </form>
          </div>

          {/* Right - Contact Information */}
          <div className="relative">
            <div className="sticky top-24">
              <div className="relative aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-xl mb-8">
                <img
                  src="https://static.readdy.ai/image/096be9c57cd562040b50de9c711536fc/a212436f95ba17ff170a8b5c05c5049a.png"
                  alt="Mow and Snow service trucks"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Serving All Greater Toronto Area</h3>
                  <p className="text-white/90">Professional service you can trust, year-round</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">Call Us</div>
                    <a
                      href="tel:+16479837924"
                      className="text-xl font-bold text-gray-900 hover:text-red-600 transition-colors cursor-pointer"
                    >
                      (647) 983-7924
                    </a>
                    <div className="text-sm text-gray-500 mt-1">24/7 Emergency Service</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-whatsapp-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">WhatsApp Messaging</div>
                    <a
                      href="https://wa.me/16479540663"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-bold text-gray-900 hover:text-green-600 transition-colors cursor-pointer"
                    >
                      (647) 954-0663
                    </a>
                    <div className="text-sm text-gray-500 mt-1">24/7 WhatsApp Chat Line</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">Email Us</div>
                    <a
                      href="mailto:info@mowandsnow.co"
                      className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer"
                    >
                      info@mowandsnow.co
                    </a>
                    <div className="text-sm text-gray-500 mt-1">Response within 24 hours</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">Service Area</div>
                    <div className="text-lg font-bold text-gray-900">Greater Toronto Area</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Toronto, Mississauga, Brampton, Vaughan & surrounding areas
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-2xl text-yellow-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">Business Hours</div>
                    <div className="text-lg font-bold text-gray-900">Mon-Fri: 7AM - 7PM</div>
                    <div className="text-sm text-gray-500 mt-1">Weekend & emergency service available</div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-600 mb-3">Follow Us</div>
                  <div className="flex items-center">
                    <a
                      href="https://www.instagram.com/mowandsnowco?igsh=MWFodGJvdzdmenVtOA%3D%3D&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-white rounded-lg hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 hover:text-white transition-all cursor-pointer"
                      title="See our work on Instagram"
                    >
                      <i className="ri-instagram-line text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
